// @ts-check
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';

import BusComponent from '../BusComponent';
import TextInput from '../form/TextInput';

const INSIGHT_TITLE_MAX_LENGTH = 60;

/**
 * @typedef Props
 * @property {import('../../types').VisualReport} visualReport
 * @property {boolean} [isEdit]
 * @property {boolean} [isCopy]
 * @property {import('react-intl').intlShape} intl
 * @property {boolean} areaSelectorEnabled
 *
 * @typedef State
 * @property {string} title
 * @property {string | null} address
 * @property {boolean} addressFetched
 *
 * @extends {BusComponent<Props, State>}
 */
class VisualReportPopup extends BusComponent {
    constructor(props, context) {
        super(props, context);
        this.state = {
            title: this.props.visualReport.title,
            address: null,
            addressFetched: false,
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            REVERSE_GEOCODE_RESPONSE: this.onReverseGeocode,
        });
        this.emit('REVERSE_GEOCODE_REQUEST', {
            point: this.props.visualReport.point,
        });
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onReverseGeocode = ({ point, address }) => {
        const { visualReport, intl } = this.props;
        if (point.lng !== visualReport.point.lng || point.lat !== visualReport.point.lat) return;

        this.setState({
            address: address || intl.formatMessage({ id: 'userLocation.defaultLocationName' }),
            addressFetched: true,
        });
    };

    onSave = () => {
        const { title, address } = this.state;
        if (!title.trim()) return;

        const { visualReport, isEdit } = this.props;

        if (isEdit) {
            this.emit('VISUAL_REPORT_UPDATE_REQUEST', {
                reportId: visualReport.id,
                title,
                selection: visualReport.selection,
                profile: visualReport.profile,
            });
        } else {
            this.emit('VISUAL_REPORT_SAVE_REQUEST', {
                visualReport: {
                    ...visualReport,
                    title,
                    address,
                },
            });
        }
        this.emit('CLOSE_MODAL');
    };

    onClickCancel = () => {
        this.emit('CLOSE_MODAL');
    };

    onClickCopy = () => {
        this.emit('CLOSE_MODAL');
        const newTitle = `Copy of ${this.props.visualReport.title}`;
        this.emit('VISUAL_REPORT_COPY_POPUP_REQUEST', {
            visualReport: {
                ...this.props.visualReport,
                title: newTitle,
            },
            areaSelectorEnabled: this.props.areaSelectorEnabled,
        });
        this.setState({
            title: newTitle,
        });
    };

    onTitleChanged = value => {
        this.setState({
            title: value,
        });
    };

    onAreaClicked = () => {
        this.emit('OPEN_AREA_SELECTOR_REQUEST');
        this.emit('CLOSE_MODAL');
    };

    render() {
        const { visualReport, isEdit, isCopy, intl, areaSelectorEnabled } = this.props;
        const { title, address, addressFetched } = this.state;

        const disabled = !(title && title.trim());
        const classes = classNames('btn-raised', { disabled });
        const selectionArray = Array.from(visualReport.selection).sort((a, b) => a - b);

        const unit =
            visualReport.profile === 'driving' ? (
                <FormattedMessage id="min" />
            ) : (
                <FormattedMessage
                    id="mi"
                    values={{
                        count:
                            selectionArray.length > 1 ? selectionArray.length : selectionArray[0],
                    }}
                />
            );

        const icon = visualReport.profile === 'driving' ? 'drive_eta' : 'album';
        const addressText = addressFetched
            ? address.label
            : intl.formatMessage({ id: 'userLocation.defaultLocationName' });

        const saveTextId = isEdit ? 'saveChanges' : isCopy ? 'saveCopy' : 'save';

        const areaContent = (
            <div className="save-as-project-details__value body-2">
                <span>{selectionArray.join(', ')}</span> {unit}
            </div>
        );
        const areaElement = areaSelectorEnabled ? (
            <button className="btn-flat" onClick={this.onAreaClicked}>
                {areaContent}
            </button>
        ) : (
            areaContent
        );

        return (
            <div className="save-as-project-content">
                <TextInput
                    initialFocus
                    className="new-project-title"
                    value={title}
                    placeholder={intl.formatMessage({
                        id: 'untitled',
                    })}
                    onChange={this.onTitleChanged}
                    onEnter={this.onSave}
                    label={intl.formatMessage({ id: 'title' })}
                    maxLength={INSIGHT_TITLE_MAX_LENGTH}
                />

                <div className="save-as-project-details">
                    <div className="save-as-project-details__row">
                        <div className="save-as-project-details__label">
                            <h6>{intl.formatMessage({ id: 'points.address' })}:</h6>
                        </div>
                        <i className="google-map-pin" />
                        <div className="save-as-project-details__value body-2">
                            <span>{addressText}</span>
                        </div>
                    </div>

                    <div className="save-as-project-details__row">
                        <div className="save-as-project-details__label">
                            <h6>{intl.formatMessage({ id: 'area' })}:</h6>
                        </div>
                        <i className="material-symbols-filled">{icon}</i>

                        {areaElement}
                    </div>
                </div>

                <div className="save-as-project-actions flex-it center space-between">
                    <div>
                        {isEdit && (
                            <button className="btn-flat" onClick={this.onClickCopy}>
                                {intl.formatMessage({ id: 'saveCopy' })}...
                            </button>
                        )}
                    </div>
                    <div className="flex-it">
                        <button className="btn-flat" onClick={this.onClickCancel}>
                            {intl.formatMessage({ id: 'cancel' })}
                        </button>
                        <button className={classes} disabled={disabled} onClick={this.onSave}>
                            {intl.formatMessage({ id: saveTextId })}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default injectIntl(VisualReportPopup);
